import { useState, useEffect } from 'react';

// Type for the place data returned from your server
interface Place {
  name: string;
  rating: number;
  vicinity: string;
  icon: string;
  url: string;
}

// Determine the base URL based on environment
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3000'; // Local development server
  }
  return 'https://wswg.blakehardee.com'; // Production URL
};

// Mock test data for development mode
const mockPlaces: Place[] = [
  {
    name: "Test Cafe",
    rating: 4.5,
    vicinity: "123 Main St, Test City",
    icon: '',
    url: 'wswg.blakehardee.com'
  },
  {
    name: "Sample Bar",
    rating: 4.0,
    vicinity: "456 Sample Rd, Test City",
    icon: '',
    url: 'https://blakehardee.com'
  },
  {
    name: "Mock Restaurant",
    rating: 3.8,
    vicinity: "789 Fake Ave, Test City",
    icon: '',
    url: ''
  },
  {
    name: "Sunset Lounge",
    rating: 4.2,
    vicinity: "101 Sunset Blvd, Test City",
    icon: '',
    url: ''
  },
  {
    name: "Green Leaf Diner",
    rating: 4.7,
    vicinity: "555 Greenway Rd, Test City",
    icon: '',
    url: ''
  },
  {
    name: "The Cozy Corner",
    rating: 4.6,
    vicinity: "88 Comfort Ln, Test City",
    icon: '',
    url: ''
  },
  {
    name: "Urban Bites",
    rating: 3.9,
    vicinity: "202 Downtown St, Test City",
    icon: '',
    url: ''
  },
  {
    name: "Blue Moon Brewery",
    rating: 4.3,
    vicinity: "333 Brewer Ave, Test City",
    icon: '',
    url: ''
  },
  {
    name: "Ocean View Restaurant",
    rating: 4.8,
    vicinity: "777 Beachfront Dr, Test City",
    icon: '',
    url: ''
  },
  {
    name: "Mountain Top Cafe",
    rating: 4.1,
    vicinity: "999 Summit Rd, Test City",
    icon: '',
    url: ''
  }
];


const usePlaces = (keyword: string, radius: string, type: string, shouldSearch: boolean) => {
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Function to get the user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLat(position.coords.latitude);
          setLng(position.coords.longitude);
        },
        (error) => {
          setError('Failed to retrieve your location, please ensure location services are enabled on your device.');
          console.error(error);
        }
      );
    } else {
      setError('Geolocation is not supported by this browser.');
    }
  };
  
  useEffect(() => {
    getCurrentLocation(); // Get user's location when the component mounts
  }, []);
  
  useEffect(() => {
    // Fetch places only if we have the latitude and longitude and the user wants to search
    if (lat && lng && shouldSearch) {
      // ✅ Use mock data in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log("Using mock places data");
        setPlaces(mockPlaces);
        return;
      }

      const fetchPlaces = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
          const baseUrl = getBaseUrl();
          if (radius === '') radius = '3';
          
          const url = `${baseUrl}/api/places?keyword=${encodeURIComponent(keyword)}&lat=${lat}&lng=${lng}&radius=${Number(radius)/0.000621371}&type=${encodeURIComponent(type)}`;
          
          console.log(`Fetching places from: ${url}`);
          
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Error fetching places: ${response.status}`);
          }
          
          const data = await response.json();
          console.log(data);
          
          if (data.results && data.results.length > 0) {
            // ✅ Manually filter open places
            const openPlaces = data.results.filter((place: any) => place.opening_hours?.open_now);
            setPlaces(openPlaces.length > 0 ? openPlaces : []);
            if (openPlaces.length === 0) setError('No open places found matching your criteria');
          } else {
            setPlaces([]);
            setError('No places found matching your criteria');
          }
        } catch (err) {
          console.error('Error fetching places:', err);
          setError('Failed to fetch places. Please try again.');
          setPlaces(null);
        } finally {
          setIsLoading(false);
        }
      };
      
      
      fetchPlaces();
    }
  }, [keyword, radius, type, lat, lng, shouldSearch]);
  
  return { places, error, isLoading, lat, lng };
};

export default usePlaces;
